import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Skeleton,
  Stack,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAppSelector } from '../../../hooks/useAppSelector';
import {
  fetchConversationMessages,
  selectConversationMessages,
  selectCreatedConversation,
  selectCreatingConversation,
  selectLoadingConversation,
  selectSelectedConversationID,
  selectSendingMessage,
  selectSendingMessageContent,
  selectSentMessage,
  setSelectedConversationID,
} from '../../../data/ai-services.slice';
import { useTranslation } from 'react-i18next';
import {
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  ContentCopy as ContentCopyIcon,
  Link as LinkIcon,
  Timer as TimerIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { ConversationMessage } from '../../../data/models/ai-conversation.model';
import { useAppDispatch } from '../../../hooks/useAppDispatch';

const PREFIX = 'Messages';

const componentClasses = {
  wrapper: `${PREFIX}-wrapper`,
  userMessage: `${PREFIX}-userMessage`,
  aiMessage: `${PREFIX}-aiMessage`,
  messageFeedback: `${PREFIX}-messageFeedback`,
  loadingMessage: `${PREFIX}-loadingMessage`,
};

const StyledMessagesBox = styled(Box)(({ theme }) => ({
  [`&.${componentClasses.wrapper}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    gap: theme.spacing(2),
    backgroundColor: '#fff',
  },
  [`& .${componentClasses.userMessage}`]: {
    alignSelf: 'flex-end',
    justifyItems: 'flex-end',

    [`& .MuiTypography-root`]: {
      padding: theme.spacing(1, 2),
      borderRadius: '50px',
      backgroundColor: theme.palette.action.disabled,
    },
  },
  [`& .${componentClasses.aiMessage}`]: {
    alignSelf: 'flex-start',
    backgroundColor: theme.palette.background.paper,
  },
  [`& .${componentClasses.messageFeedback}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
  },
  [`& .${componentClasses.loadingMessage}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
}));

const Messages = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const messages = useAppSelector(selectConversationMessages);
  const selectedConversationID = useAppSelector(selectSelectedConversationID);
  const loadingConversation = useAppSelector(selectLoadingConversation);
  const sendingMessage = useAppSelector(selectSendingMessage);
  const sendingMessageContent = useAppSelector(selectSendingMessageContent);
  const sentMessage = useAppSelector(selectSentMessage);
  const creatingConversation = useAppSelector(selectCreatingConversation);
  const createdConversation = useAppSelector(selectCreatedConversation);

  const [copyMessageTooltipOpenId, setCopyMessageTooltipOpenId] = useState<
    string | null
  >(null);
  const [copyLinkTooltipOpenId, setCopyLinkTooltipOpenId] = useState<
    string | null
  >(null);
  const [linkCopiedMessageId, setLinkCopiedMessageId] = useState<string | null>(
    null,
  );
  const [textCopiedMessageId, setTextCopiedMessageId] = useState<string | null>(
    null,
  );

  const handleCopyMessageLink = (message: ConversationMessage) => {
    const url = `${window.location.origin}/ai-services/conversation/${message.conversationSessionID}/message/${message.conversationMessageID}`;
    navigator.clipboard.writeText(url);
    setLinkCopiedMessageId(message.conversationMessageID);
    setCopyLinkTooltipOpenId(message.conversationMessageID);
  };

  const handleCopyMessageText = (message: ConversationMessage) => {
    navigator.clipboard.writeText(message.response);
    setTextCopiedMessageId(message.conversationMessageID);
    setCopyMessageTooltipOpenId(message.conversationMessageID);
  };

  const parseMessageContent = (message: ConversationMessage) => {
    const lines = message.response.split('\n');
    const content = lines.map((line) => (
      <Typography key={line}>{line.length > 0 ? line : '\u00A0'}</Typography>
    ));
    return content;
  };

  useEffect(() => {
    if (createdConversation && selectedConversationID) {
      dispatch(fetchConversationMessages(selectedConversationID));
    }
  }, [createdConversation, selectedConversationID]);

  useEffect(() => {
    // Scroll to bottom when messages change
    const messagesContainer = document.querySelector(
      `.${componentClasses.wrapper}`,
    );
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }, [messages.length]);

  useEffect(() => {
    if (linkCopiedMessageId) {
      setTimeout(() => {
        setLinkCopiedMessageId(null);
        setCopyLinkTooltipOpenId(null);
      }, 4000);
    }
  }, [linkCopiedMessageId]);

  useEffect(() => {
    if (textCopiedMessageId) {
      setTimeout(() => {
        setTextCopiedMessageId(null);
        setCopyMessageTooltipOpenId(null);
      }, 4000);
    }
  }, [textCopiedMessageId]);

  if (loadingConversation) {
    return (
      <StyledMessagesBox className={componentClasses.wrapper}>
        <Box className={componentClasses.loadingMessage}>
          <CircularProgress />
        </Box>
      </StyledMessagesBox>
    );
  }

  return (
    <StyledMessagesBox className={componentClasses.wrapper}>
      {creatingConversation && sendingMessageContent && (
        <>
          <Box className={componentClasses.userMessage}>
            <Typography>{sendingMessageContent.question}</Typography>
          </Box>
          <Box className={componentClasses.aiMessage}>
            <CircularProgress />
          </Box>
        </>
      )}
      {selectedConversationID &&
        messages.length > 0 &&
        messages.map((message) => (
          <React.Fragment key={message.conversationMessageID}>
            <Box className={componentClasses.userMessage}>
              <Typography>{message.question}</Typography>
            </Box>
            <Box className={componentClasses.aiMessage}>
              {parseMessageContent(message)}
              {message.responseSQL && (
                <Typography>{message.responseSQL}</Typography>
              )}
            </Box>
            <Stack
              direction="row"
              spacing={0}
              className={componentClasses.messageFeedback}>
              <IconButton
                aria-label={
                  textCopiedMessageId === message.conversationMessageID
                    ? t('aiServices.messageFeedback.copied')
                    : t('aiServices.messageFeedback.copy')
                }
                onClick={() => handleCopyMessageText(message)}
                onMouseEnter={() =>
                  setCopyMessageTooltipOpenId(message.conversationMessageID)
                }
                onMouseLeave={() => {
                  if (textCopiedMessageId !== message.conversationMessageID) {
                    setCopyMessageTooltipOpenId(null);
                  }
                }}
                size="small">
                <Tooltip
                  placement="top"
                  arrow
                  open={
                    copyMessageTooltipOpenId === message.conversationMessageID
                  }
                  disableHoverListener
                  disableFocusListener
                  title={
                    textCopiedMessageId === message.conversationMessageID
                      ? t('aiServices.messageFeedback.copied')
                      : t('aiServices.messageFeedback.copy')
                  }>
                  <ContentCopyIcon fontSize="inherit" />
                </Tooltip>
              </IconButton>
              <IconButton
                aria-label={t('aiServices.messageFeedback.goodResponse')}
                disabled={true}
                size="small">
                <Tooltip
                  placement="top"
                  arrow
                  title={t('aiServices.messageFeedback.goodResponse')}>
                  <ThumbUpIcon fontSize="inherit" />
                </Tooltip>
              </IconButton>
              <IconButton
                aria-label={t('aiServices.messageFeedback.badResponse')}
                disabled={true}
                size="small">
                <Tooltip
                  placement="top"
                  arrow
                  title={t('aiServices.messageFeedback.badResponse')}>
                  <ThumbDownIcon fontSize="inherit" />
                </Tooltip>
              </IconButton>
              <IconButton
                aria-label={
                  linkCopiedMessageId
                    ? t('aiServices.messageFeedback.linkCopied')
                    : t('aiServices.messageFeedback.responseDuration', {
                        duration: message.responseDurationSeconds,
                      })
                }
                onClick={() => handleCopyMessageLink(message)}
                onMouseEnter={() =>
                  setCopyLinkTooltipOpenId(message.conversationMessageID)
                }
                onMouseLeave={() => {
                  if (linkCopiedMessageId !== message.conversationMessageID) {
                    setCopyLinkTooltipOpenId(null);
                  }
                }}
                size="small">
                <Tooltip
                  placement="top"
                  arrow
                  open={copyLinkTooltipOpenId === message.conversationMessageID}
                  disableHoverListener
                  disableFocusListener
                  title={
                    linkCopiedMessageId
                      ? t('aiServices.messageFeedback.linkCopied')
                      : t('aiServices.messageFeedback.copyLink')
                  }>
                  <LinkIcon fontSize="inherit" />
                </Tooltip>
              </IconButton>
              <IconButton
                aria-label={t('aiServices.messageFeedback.responseDuration', {
                  duration: message.responseDurationSeconds,
                })}
                size="small">
                <Tooltip
                  placement="top"
                  arrow
                  title={t('aiServices.messageFeedback.responseDuration', {
                    duration: message.responseDurationSeconds,
                  })}>
                  <InfoIcon fontSize="inherit" />
                </Tooltip>
              </IconButton>
            </Stack>
          </React.Fragment>
        ))}
      {sendingMessage && sendingMessageContent && (
        <>
          <Box className={componentClasses.userMessage}>
            <Typography>{sendingMessageContent.question}</Typography>
          </Box>
          <Box className={componentClasses.aiMessage}>
            <CircularProgress />
          </Box>
        </>
      )}
      {(!selectedConversationID || messages.length === 0) &&
        !creatingConversation &&
        !sendingMessage && (
          <Typography>{t('aiServices.newConversationTitle')}</Typography>
        )}
    </StyledMessagesBox>
  );
};

export { Messages };
